import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Copy, Download, KeyRound, LoaderCircle, Lock, LockOpen, RotateCcw, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { WrongKeyError, generateRecoveryKey, passphraseProblems } from "@brainlog/shared";
import { errorMessage } from "@/lib/api";
import { useMe } from "@/lib/queries";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getPersonalKey, lockAll, unlockPersonalKey, usePersonalUnlocked } from "./keyring";
import { changePersonalSecret, createPersonalKey, personalKeyQuery, useFingerprint, usePersonalKeyRecord } from "./personal";
import { saveBlob } from "./ops";

function RecoveryKeyBox({ value, label }: { value: string; label: string }) {
  return (
    <div className="flex items-center gap-2 rounded-lg border bg-muted/50 p-3">
      <code className="flex-1 break-all font-mono text-sm tracking-wide" data-testid="personal-recovery-key">
        {value}
      </code>
      <Button
        type="button"
        size="icon-sm"
        variant="ghost"
        aria-label="Copy recovery key"
        onClick={() => navigator.clipboard.writeText(value).then(() => toast.success("Recovery key copied"), () => toast.error("Copy failed — select and copy it manually."))}
      >
        <Copy />
      </Button>
      <Button
        type="button"
        size="icon-sm"
        variant="ghost"
        aria-label="Download recovery key"
        onClick={() => saveBlob(new Blob([`BrainLog personal recovery key\n${label}\nCreated: ${new Date().toISOString()}\n\n${value}\n`], { type: "text/plain" }), "brainlog-personal-recovery-key.txt")}
      >
        <Download />
      </Button>
    </div>
  );
}

/** Create the personal key: a passphrase (and a recovery key) only this person knows. */
export function PersonalKeySetup({ onDone, replace = false, compact = false }: { onDone?(): void; replace?: boolean; compact?: boolean }) {
  const me = useMe();
  const qc = useQueryClient();
  const [pass, setPass] = useState("");
  const [confirm, setConfirm] = useState("");
  const [useRecovery, setUseRecovery] = useState(true);
  const [step, setStep] = useState<"passphrase" | "recovery">("passphrase");
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const recovery = useMemo(() => generateRecoveryKey(), []);
  const problems = passphraseProblems(pass);
  const mismatch = confirm.length > 0 && confirm !== pass;

  const create = async () => {
    if (!me.data) return;
    setBusy(true);
    setError(null);
    try {
      await createPersonalKey(me.data.id, pass, useRecovery ? recovery : null, replace);
      await qc.invalidateQueries({ queryKey: personalKeyQuery });
      await qc.invalidateQueries({ queryKey: ["scope-access"] });
      toast.success("Your personal key is ready and unlocked in this tab.");
      onDone?.();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (step === "recovery")
    return (
      <div className="space-y-3 text-sm">
        <p>Store this personal recovery key somewhere safe (a password manager or printed). It is shown only now.</p>
        <RecoveryKeyBox value={recovery} label={me.data?.displayName ?? ""} />
        <label className="flex items-start gap-2">
          <Checkbox checked={saved} onCheckedChange={(v) => setSaved(v === true)} className="mt-0.5" />
          <span>I stored my personal recovery key somewhere safe.</span>
        </label>
        {error && (
          <p role="alert" className="text-destructive">
            {error}
          </p>
        )}
        <div className="flex gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={() => setStep("passphrase")} disabled={busy}>
            Back
          </Button>
          <Button type="button" size="sm" disabled={!saved || busy} onClick={() => void create()}>
            {busy ? <LoaderCircle className="animate-spin" /> : <KeyRound />} Create my personal key
          </Button>
        </div>
      </div>
    );

  return (
    <form
      className="space-y-3 text-sm"
      onSubmit={(e) => {
        e.preventDefault();
        if (problems.length || mismatch || !confirm) return;
        if (useRecovery) setStep("recovery");
        else void create();
      }}
    >
      {!compact && (
        <p className="text-muted-foreground">
          Your personal key lets others share encrypted notebooks, projects and notes with you — and you with them — without passing passphrases around. You open everything shared with
          you with this one passphrase.
        </p>
      )}
      <div className="flex flex-wrap gap-3">
        <div className="space-y-1">
          <Label htmlFor="pk-pass">Personal passphrase</Label>
          <Input id="pk-pass" type="password" autoComplete="new-password" value={pass} onChange={(e) => setPass(e.target.value)} className="w-64" aria-describedby="pk-pass-help" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="pk-pass2">Repeat</Label>
          <Input id="pk-pass2" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="w-64" aria-invalid={mismatch} />
        </div>
      </div>
      <p id="pk-pass-help" className="text-xs text-muted-foreground">
        {mismatch ? "The passphrases do not match." : pass && problems.length ? problems.join(" ") : "Only you know it. It never leaves this browser."}
      </p>
      <label className="flex items-start gap-2">
        <Checkbox checked={useRecovery} onCheckedChange={(v) => setUseRecovery(v === true)} className="mt-0.5" />
        <span>Also create a personal recovery key (recommended) — it opens your key if you forget the passphrase.</span>
      </label>
      {replace && (
        <Alert variant="destructive">
          <TriangleAlert />
          <AlertTitle>Starting over</AlertTitle>
          <AlertDescription>Content shared with your old key cannot be opened with the new one. Members who can open it have to give you access again.</AlertDescription>
        </Alert>
      )}
      {error && (
        <p role="alert" className="text-destructive">
          {error}
        </p>
      )}
      <Button type="submit" size="sm" disabled={busy || !!problems.length || !confirm || mismatch}>
        {busy ? <LoaderCircle className="animate-spin" /> : <KeyRound />} {useRecovery ? "Next" : "Create my personal key"}
      </Button>
    </form>
  );
}

/** Unlock the personal key in this tab. */
export function PersonalKeyUnlock({ onDone }: { onDone?(): void }) {
  const me = useMe();
  const record = usePersonalKeyRecord();
  const [secret, setSecret] = useState("");
  const [kind, setKind] = useState<"passphrase" | "recovery">("passphrase");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasRecovery = !!record.data?.slots.some((s) => s.kind === "recovery");
  return (
    <form
      className="flex flex-wrap items-end gap-2 text-sm"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!record.data || !me.data || !secret) return;
        setBusy(true);
        setError(null);
        try {
          await unlockPersonalKey(record.data, me.data.id, secret, kind);
          setSecret("");
          onDone?.();
        } catch (err) {
          setError(err instanceof WrongKeyError ? (kind === "passphrase" ? "That is not your personal passphrase." : "That recovery key is not correct.") : (err as Error).message);
        } finally {
          setBusy(false);
        }
      }}
    >
      <div className="space-y-1">
        <Label htmlFor="pk-unlock">{kind === "passphrase" ? "Your personal passphrase" : "Your personal recovery key"}</Label>
        <Input
          id="pk-unlock"
          type={kind === "passphrase" ? "password" : "text"}
          autoComplete="off"
          value={secret}
          onChange={(e) => setSecret(e.target.value)}
          className="w-64"
          aria-invalid={!!error}
        />
      </div>
      <Button type="submit" size="sm" disabled={busy || !secret}>
        {busy ? <LoaderCircle className="animate-spin" /> : <LockOpen />} Unlock
      </Button>
      {hasRecovery && (
        <Button type="button" variant="link" size="sm" className="px-0" onClick={() => (setKind(kind === "passphrase" ? "recovery" : "passphrase"), setSecret(""), setError(null))}>
          {kind === "passphrase" ? "Use your recovery key" : "Use your passphrase"}
        </Button>
      )}
      {error && (
        <p role="alert" className="w-full text-destructive">
          {error}
        </p>
      )}
    </form>
  );
}

/** Settings: the person's own key — set up, unlock/lock, fingerprint, change passphrase, new recovery key. */
export function PersonalKeySettings() {
  const me = useMe();
  const qc = useQueryClient();
  const record = usePersonalKeyRecord();
  const unlocked = usePersonalUnlocked();
  const fp = useFingerprint(record.data?.publicKey);
  const [mode, setMode] = useState<null | "passphrase" | "replace">(null);
  const [pass, setPass] = useState("");
  const [confirm, setConfirm] = useState("");
  const [newRecovery, setNewRecovery] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (record.isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;

  if (!record.data || mode === "replace")
    return (
      <div className="space-y-2">
        {mode === "replace" && (
          <Button size="sm" variant="ghost" onClick={() => setMode(null)}>
            Cancel
          </Button>
        )}
        <PersonalKeySetup compact replace={mode === "replace"} onDone={() => setMode(null)} />
      </div>
    );

  const problems = passphraseProblems(pass);
  return (
    <div className="space-y-3 text-sm">
      <p className="flex flex-wrap items-center gap-2">
        {unlocked ? <LockOpen className="size-4 text-primary" aria-hidden /> : <Lock className="size-4" aria-hidden />}
        Your personal key is set up and <strong>{unlocked ? "unlocked in this tab" : "locked"}</strong>.
      </p>
      <p className="text-xs text-muted-foreground">
        Fingerprint: <code className="font-mono">{fp.data ?? "…"}</code> — people who share keys with you see the same fingerprint next to your name; compare it with them if you want to be sure
        nobody swapped your key.
      </p>
      {!unlocked ? (
        <div className="space-y-2">
          <PersonalKeyUnlock />
          <Button size="sm" variant="link" className="px-0 text-destructive" onClick={() => setMode("replace")}>
            <RotateCcw /> Lost your passphrase and recovery key? Start over with a new personal key…
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={lockAll}>
              <Lock /> Lock now
            </Button>
            <Button size="sm" variant="outline" onClick={() => setMode(mode === "passphrase" ? null : "passphrase")}>
              <KeyRound /> Change personal passphrase
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={async () => {
                const k = getPersonalKey();
                if (!k || !me.data) return;
                if (!window.confirm("Create a new personal recovery key? The previous one stops working.")) return;
                const key = generateRecoveryKey();
                setBusy(true);
                try {
                  await changePersonalSecret(me.data.id, k.pkcs8, key, "recovery");
                  await qc.invalidateQueries({ queryKey: personalKeyQuery });
                  setNewRecovery(key);
                } catch (err) {
                  toast.error(errorMessage(err));
                } finally {
                  setBusy(false);
                }
              }}
            >
              <KeyRound /> New recovery key
            </Button>
          </div>
          {newRecovery && (
            <div className="space-y-1">
              <p>Your new personal recovery key — store it somewhere safe; it is shown only now:</p>
              <RecoveryKeyBox value={newRecovery} label={me.data?.displayName ?? ""} />
            </div>
          )}
          {mode === "passphrase" && (
            <form
              className="flex flex-wrap items-end gap-2 rounded-md border p-3"
              onSubmit={async (e) => {
                e.preventDefault();
                const k = getPersonalKey();
                if (!k || !me.data || problems.length || pass !== confirm) return;
                setBusy(true);
                try {
                  await changePersonalSecret(me.data.id, k.pkcs8, pass, "passphrase");
                  await qc.invalidateQueries({ queryKey: personalKeyQuery });
                  toast.success("Personal passphrase changed.");
                  setMode(null);
                  setPass("");
                  setConfirm("");
                } catch (err) {
                  toast.error(errorMessage(err));
                } finally {
                  setBusy(false);
                }
              }}
            >
              <div className="space-y-1">
                <Label htmlFor="pk-new">New personal passphrase</Label>
                <Input id="pk-new" type="password" autoComplete="new-password" value={pass} onChange={(e) => setPass(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="pk-new2">Repeat</Label>
                <Input id="pk-new2" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
              </div>
              <Button size="sm" type="submit" disabled={busy || !!problems.length || pass !== confirm}>
                Save
              </Button>
              {pass && problems.length > 0 && <p className="w-full text-xs text-muted-foreground">{problems.join(" ")}</p>}
            </form>
          )}
        </div>
      )}
    </div>
  );
}
